import { Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useLiveContacts } from './hooks/useLiveContacts'
import { useIsMobile } from './hooks/useIsMobile'
import { usePageMeta } from './hooks/usePageMeta'
import ContactMap from './components/map/ContactMap'
import ContactLayer from './components/map/ContactLayer'
import Sidebar from './components/layout/Sidebar'
import MobileSheet from './components/layout/MobileSheet'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import AboutPage from './pages/AboutPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import TermsPage from './pages/TermsPage'
import PrivacyPage from './pages/PrivacyPage'
import LedgerVerifyPage from './pages/LedgerVerifyPage'
import ProfessionalTierPage from './pages/ProfessionalTierPage'
import MyReportsPage from './pages/MyReportsPage'
import AdminLayout from './pages/admin/AdminLayout'
import AdminOverviewPage from './pages/admin/AdminOverviewPage'
import AdminUsersPage from './pages/admin/AdminUsersPage'
import AdminProfessionalRequestsPage from './pages/admin/AdminProfessionalRequestsPage'
import AdminFlaggedReportsPage from './pages/admin/AdminFlaggedReportsPage'
import AdminContactsPage from './pages/admin/AdminContactsPage'
import AdminCheckpointsPage from './pages/admin/AdminCheckpointsPage'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 2, staleTime: 30_000 },
  },
})

function MapPage() {
  // Public — anyone can view fused contacts without an account. Only submitting a report
  // requires login (see Sidebar/MobileSheet's "Sign in to report" gate).
  usePageMeta(
    'Biosecurity Reporter',
    'Crowdsourced H5N1 avian influenza contact reporting for Australia. Sightings are cross-verified into a confidence-scored map — not an official biosecurity channel.',
    '/',
  )
  useLiveContacts()
  const isMobile = useIsMobile()

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-gray-50">
      {isMobile ? (
        <div className="flex h-full w-full relative">
          <ContactMap className="flex-1">
            <ContactLayer />
          </ContactMap>
          <MobileSheet />
        </div>
      ) : (
        <div className="flex h-full w-full">
          <Sidebar />
          <ContactMap className="flex-1">
            <ContactLayer />
          </ContactMap>
        </div>
      )}
    </div>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        <Route path="/" element={<MapPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/ledger/verify" element={<LedgerVerifyPage />} />
        <Route path="/professional-tier" element={<ProfessionalTierPage />} />
        <Route path="/my-reports" element={<MyReportsPage />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminOverviewPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="professional-requests" element={<AdminProfessionalRequestsPage />} />
          <Route path="flagged-reports" element={<AdminFlaggedReportsPage />} />
          <Route path="contacts" element={<AdminContactsPage />} />
          <Route path="checkpoints" element={<AdminCheckpointsPage />} />
        </Route>
        <Route path="*" element={<MapPage />} />
      </Routes>
    </QueryClientProvider>
  )
}
